import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { CloseIcon } from "../atoms/Icon";
import Image from "../atoms/Image";
import LanguageSelector from "../molecules/LanguageSelector/LanguageSelector";

interface MobileMenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  logoSrc: string;
  currentLanguage: string;
  onLanguageChange: (lang: string) => void;
}

const FOCUSABLE = 'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])';

const MobileMenuDrawer: React.FC<MobileMenuDrawerProps> = ({
  isOpen,
  onClose,
  logoSrc,
  currentLanguage,
  onLanguageChange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const languages = [
    { code: "es", label: "Español" },
    { code: "en", label: "English" },
  ];

  // Move focus into drawer when opened, restore when closed
  useEffect(() => {
    if (isOpen) {
      closeButtonRef.current?.focus();
    }
  }, [isOpen]);

  // Trap focus inside drawer while open
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key !== "Tab") return;
      const focusable = Array.from(
        drawerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen]);

  const navigateToSection = (sectionId: string) => {
    onClose();
    const targetHash = `#${sectionId}`;

    if (window.location.pathname === "/") {
      if (window.location.hash !== targetHash) {
        window.history.pushState(null, "", `/${targetHash}`);
      }
      window.setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
      return;
    }

    navigate(`/${targetHash}`);
  };

  return (
    <>
      {/* Backdrop — aria-hidden so screen readers don't announce it */}
      <div
        className={`md:hidden fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300 z-40 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("layout.openMenu")}
        className={`md:hidden fixed top-0 right-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <Image src={logoSrc} alt="Surevision" className="h-8" width="128" height="32" />
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
            aria-label={t("layout.closeMenu")}
          >
            <CloseIcon />
          </button>
        </div>

        <nav className="p-6">
          <ul className="space-y-6">
            <li>
              <Link
                to="/"
                className="block text-lg font-medium text-gray-900 hover:text-blue-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
                onClick={onClose}
              >
                {t("layout.home")}
              </Link>
            </li>
            <li>
              <Link
                to="/team"
                className="block text-lg font-medium text-gray-900 hover:text-blue-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
                onClick={onClose}
              >
                {t("layout.team")}
              </Link>
            </li>
            <li>
              <Link
                to="/contact"
                className="block text-lg font-medium text-gray-900 hover:text-blue-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
                onClick={onClose}
              >
                {t("layout.contact")}
              </Link>
            </li>
          </ul>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-500 mb-3">{t("layout.language")}</p>
            <LanguageSelector
              languages={languages}
              currentLanguage={currentLanguage}
              onLanguageChange={(lang) => {
                onLanguageChange(lang);
                onClose();
              }}
              variant="mobile"
            />
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <ul className="space-y-4">
              <li>
                <button
                  type="button"
                  className="block w-full text-left text-base text-gray-600 hover:text-blue-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
                  onClick={() => navigateToSection("Mission")}
                >
                  {t("nav.ourMission")}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="block w-full text-left text-base text-gray-600 hover:text-blue-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
                  onClick={() => navigateToSection("Vision")}
                >
                  {t("nav.ourVision")}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="block w-full text-left text-base text-gray-600 hover:text-blue-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
                  onClick={() => navigateToSection("Values")}
                >
                  {t("nav.ourValues")}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="block w-full text-left text-base text-gray-600 hover:text-blue-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
                  onClick={() => navigateToSection("Product")}
                >
                  {t("nav.ourInnovation")}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="block w-full text-left text-base text-gray-600 hover:text-blue-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
                  onClick={() => navigateToSection("Benefits")}
                >
                  {t("nav.ourAdvantages")}
                </button>
              </li>
            </ul>
          </div>
        </nav>
      </div>
    </>
  );
};

export default MobileMenuDrawer;
